import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { take, takeUntil } from 'rxjs';
import { HasSubscription } from 'src/ca-shared/has-subscription/has-subscription';
import { AutoUnsubscribe } from 'src/core/decorators/auto-unsubscribe.decorator';
import { IntegerValidator } from 'src/core/validators/shared/integer.validator';

@Component({
  selector: 'ca-automatic-summarization-settings',
  templateUrl: './automatic-summarization-settings.component.html',
  styleUrls: ['./automatic-summarization-settings.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  standalone: false,
})
@AutoUnsubscribe()
export class AutomaticSummarizationSettingsComponent
  extends HasSubscription
  implements OnInit, OnChanges
{
  @Input() maxDailyConversationLimit: number = Number.MAX_SAFE_INTEGER;

  get form(): FormGroup {
    return this.formGroupDirective.form;
  }

  get isAutomaticSummarizationDisabled(): boolean {
    return !this.form.get('automaticSummarizationEnabled')?.value;
  }

  constructor(private formGroupDirective: FormGroupDirective) {
    super();
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(take(1)).subscribe(value => {
      this.setValidators(value);
    });

    const automaticSummarizationEnabledControl = this.form.get('automaticSummarizationEnabled');
    this.reconcileDependentSettings(automaticSummarizationEnabledControl.value);
    automaticSummarizationEnabledControl.valueChanges
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .subscribe(value => this.reconcileDependentSettings(value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maxDailyConversationLimit'] && !changes['maxDailyConversationLimit'].firstChange) {
      this.setValidators(this.form.value);
    }
  }

  private reconcileDependentSettings(automaticSummarizationEnabled: boolean): void {
    if (!automaticSummarizationEnabled) {
      this.form.get('aiCategoryDiscoveryEnabled').patchValue(false);
      this.form.get('aiCoachingEnabled').patchValue(false);
    }
  }

  setValidators(formValues): void {
    const filter = formValues.automaticSummarizationFilterConversations.toLowerCase() === 'true';

    if (!formValues.automaticSummarizationEnabled) {
      this.form.get('automaticSummarizationCategories').clearValidators();
      this.form.get('automaticSummarizationDailyConversationLimit').clearValidators();
    } else if (formValues.automaticSummarizationEnabled && filter) {
      this.form.get('automaticSummarizationCategories').addValidators([Validators.required]);
      this.form
        .get('automaticSummarizationDailyConversationLimit')
        .addValidators([IntegerValidator.minMax(0, this.maxDailyConversationLimit)]);
    } else if (formValues.automaticSummarizationEnabled) {
      this.form
        .get('automaticSummarizationDailyConversationLimit')
        .addValidators([IntegerValidator.minMax(0, this.maxDailyConversationLimit)]);
      this.form.get('automaticSummarizationCategories').clearValidators();
    }

    this.form.updateValueAndValidity();

    this.form.valueChanges.pipe(take(1)).subscribe(value => {
      this.setValidators(value);
    });
  }
}
